import React from 'react';
import { Helmet } from 'react-helmet';

// Get base URL from environment variable, fallback to production domain
const getBaseUrl = () => {
  const envUrl = process.env.REACT_APP_SITE_URL;
  if (envUrl && envUrl.includes('brovko.de')) {
    return 'https://brovko.de';
  }
  if (envUrl) {
    return envUrl;
  }
  return 'https://brovko.de';
};

// Company information for structured data
const companyInfo = {
  name: 'BROVKO GmbH',
  alternateName: 'Brovko AI Transformation',
  description: 'BROVKO ist eine führende KI-Beratung für Enterprise AI Transformation. Wir unterstützen Unternehmen mit der KI-Value-Engine bei der messbaren Wertschöpfung durch künstliche Intelligenz.',
  url: 'https://brovko.de',
  logo: 'https://brovko.de/favicon.svg',
  email: 'email@brovko.de',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'DE',
    addressLocality: 'Germany'
  },
  sameAs: [
    'https://www.linkedin.com/company/brovko',
  ],
  areaServed: ['DE', 'AT', 'CH'],
  knowsAbout: [
    'Künstliche Intelligenz',
    'KI-Transformation',
    'Enterprise AI',
    'Machine Learning',
    'KI-Strategie',
    'AI Governance',
    'ISO 42001',
    'Private Equity AI',
    'KI-Value-Engine'
  ]
};

const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  canonicalUrl, 
  ogTitle, 
  ogDescription,
  ogImage = "/og-image.png",
  structuredData,
  noindex = false,
  pageType = 'website',
  articleData,
  faqData,
  serviceData,
  breadcrumbs
}) => {
  const baseUrl = getBaseUrl();
  const fullCanonicalUrl = canonicalUrl?.startsWith('http') 
    ? canonicalUrl 
    : `${baseUrl}${canonicalUrl?.startsWith('/') ? canonicalUrl : '/' + (canonicalUrl || '')}`;
  
  const cleanCanonicalUrl = fullCanonicalUrl.replace(/([^:])\/\//g, '$1/');
  const fullOgImage = ogImage?.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;
  
  // WebPage Schema (Organization + WebSite are defined in index.html, referenced via @id)
  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': pageType === 'article' ? 'Article' : 'WebPage',
    '@id': cleanCanonicalUrl,
    url: cleanCanonicalUrl,
    name: title,
    description: description,
    isPartOf: {
      '@id': `${baseUrl}/#website`
    },
    about: {
      '@id': `${baseUrl}/#organization`
    },
    inLanguage: 'de-DE',
    ...(articleData && {
      author: {
        '@type': 'Organization',
        name: companyInfo.name
      },
      datePublished: articleData.datePublished,
      dateModified: articleData.dateModified || articleData.datePublished
    })
  };

  // Breadcrumb Schema
  const breadcrumbSchema = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url?.startsWith('http') ? crumb.url : `${baseUrl}${crumb.url}`
    }))
  } : null;

  // FAQ Schema for GEO (Generative Engine Optimization)
  const faqSchema = faqData ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  } : null;

  // Service Schema
  const serviceSchema = serviceData ? {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: serviceData.name,
    description: serviceData.description,
    provider: {
      '@id': `${baseUrl}/#organization`
    },
    areaServed: companyInfo.areaServed,
    serviceType: serviceData.serviceType || 'AI Consulting'
  } : null;

  // Combine all schemas (Organization + WebSite live in index.html)
  const allSchemas = [
    webPageSchema,
    breadcrumbSchema,
    faqSchema,
    serviceSchema,
    structuredData
  ].filter(Boolean);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={cleanCanonicalUrl} />
      
      {/* Language */}
      <html lang="de" />
      <meta httpEquiv="content-language" content="de-DE, en-US" />
      
      {/* Robots */}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"} />
      <meta name="googlebot" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <meta name="bingbot" content={noindex ? "noindex, nofollow" : "index, follow"} />
      
      {/* GEO - Generative Engine Optimization for AI/LLMs */}
      <meta name="ai-content-declaration" content="human-created" />
      <meta name="generator" content="BROVKO GmbH" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={pageType === 'article' ? 'article' : 'website'} />
      <meta property="og:url" content={cleanCanonicalUrl} />
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="BROVKO" />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:locale:alternate" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={cleanCanonicalUrl} />
      <meta name="twitter:title" content={ogTitle || title} />
      <meta name="twitter:description" content={ogDescription || description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:site" content="@brovko" />
      <meta name="twitter:creator" content="@brovko" />
      
      {/* Additional SEO */}
      <meta name="author" content="BROVKO GmbH" />
      <meta name="publisher" content="BROVKO GmbH" />
      <meta name="copyright" content="BROVKO GmbH" />
      <meta name="geo.region" content="DE" />
      <meta name="geo.placename" content="Germany" />
      
      {/* Theme & Branding */}
      <meta name="theme-color" content="#14b8a6" />
      <meta name="msapplication-TileColor" content="#14b8a6" />
      
      {/* Structured Data - All Schemas */}
      {allSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
